import cx from 'classnames';
import dayjs from 'dayjs';
import _ from 'lodash';
import { useMemo, useState } from 'react';
import Modal from 'react-modal';
import { getAllDaysInMonth } from '../../../../../utility';
import './styles.scss';

// daysCount = 7: one week of the shown period. daysCount = 14: a whole scheduling
// period (periods are 14 days and follow each other), so any month can be browsed.
const SelectWeekModal = ({ onClose, periodData, setDates, dates, daysCount = 7 }) => {
  const isPeriodSelection = daysCount === 14;
  const initialDate = (isPeriodSelection && dates[0]) || periodData.start_date;
  const [selectedDate, setSelectedDate] = useState(new Date(initialDate.getFullYear(), initialDate.getMonth(), 1));
  const [hoveredDays, setHoveredDays] = useState([]);

  const getRangeForDay = (day) => {
    if (!day) {
      return null;
    }
    if (!isPeriodSelection && (day < periodData.start_date || day > periodData.end_date)) {
      return null;
    }

    const offset = Math.round(dayjs(day).diff(dayjs(periodData.start_date), 'day', true));
    const rangeStart = dayjs(periodData.start_date).add(Math.floor(offset / daysCount) * daysCount, 'day');

    return Array.from({ length: daysCount }, (_, i) => rangeStart.add(i, 'day').toDate());
  };

  const isAvailableDay = (day) => !!day && (isPeriodSelection || (periodData.start_date <= day && periodData.end_date >= day));

  const calendarDaysData = useMemo(() => {
    const emptyStartDays = Array.from({ length: selectedDate.getDay() }, (_, i) => ({ date: null, weekDay: i }));

    const allDays = getAllDaysInMonth(selectedDate.getMonth() + 1, selectedDate.getFullYear());
    const calendarDays = allDays.map((day) => ({
      date: day,
      weekDay: day.getDay(),
    }));

    const emptyEndDays = Array.from({ length: 6 - allDays.at(-1).getDay() }, (_, i) => ({
      date: null,
      weekDay: allDays.at(-1).getDay() + i + 1,
    }));

    return [...emptyStartDays, ...calendarDays, ...emptyEndDays];
  }, [selectedDate]);

  const handleChangeDate = (increment) => {
    setSelectedDate((prevDate) => new Date(prevDate.getFullYear(), prevDate.getMonth() + increment, 1));
  };

  const handleHoverDay = (day) => {
    const range = getRangeForDay(day);
    if (range) {
      setHoveredDays(range);
    }
  };

  const handleDayClick = (day) => {
    const range = getRangeForDay(day);
    if (range) {
      setDates(range);
    }
  };

  return (
    <Modal
      isOpen
      ariaHideApp={false}
      className="modal-default select-week-modal"
      overlayClassName="modal-overlay select-week-modal-overlay"
      onRequestClose={onClose}
    >
      <div className="modal-header">
        <div className="month-title">
          {selectedDate.toLocaleDateString('en-US', {
            month: 'long',
            year: 'numeric',
          })}
        </div>

        <div className="select-month-arrows-wrapper">
          <div className="select-month-arrow" onClick={() => handleChangeDate(-1)}>
            <svg width="8" height="14" viewBox="0 0 8 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M7 1L1 7L7 13" stroke="#101828" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>

          <div className="select-month-arrow" onClick={() => handleChangeDate(1)}>
            <svg width="8" height="14" viewBox="0 0 8 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1 13L7 7L1 1" stroke="#101828" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        </div>
      </div>

      <div className="calendar-container">
        <div className="day-name">S</div>
        <div className="day-name">M</div>
        <div className="day-name">T</div>
        <div className="day-name">W</div>
        <div className="day-name">T</div>
        <div className="day-name">F</div>
        <div className="day-name">S</div>

        {_.chunk(calendarDaysData, 7).map((row, rowIndex) =>
          row.map((item, colIndex) => (
            <div
              className={cx('calendar-day', {
                isSelected: dates.some((day) => dayjs(day).isSame(item.date, 'day')),
                isLeftEdge: dates && dayjs(dates[0]).isSame(item.date, 'day'),
                isRightEdge: dates && dayjs(dates.at(-1)).isSame(item.date, 'day'),
                isAvailable: isAvailableDay(item.date),
                isHovered: hoveredDays.some((day) => dayjs(day).isSame(item.date, 'day')),
              })}
              key={`${rowIndex} ${colIndex}`}
              onMouseEnter={() => handleHoverDay(item.date)}
              onMouseLeave={() => setHoveredDays([])}
              onClick={() => handleDayClick(item.date)}
            >
              {item.date && <div className="day-number">{item.date.getDate()}</div>}
            </div>
          )),
        )}
      </div>

      <div className="modal-footer">
        <button className="btn btn-outline" onClick={onClose}>
          Cancel
        </button>
        <button className="btn" onClick={onClose}>
          Save
        </button>
      </div>
    </Modal>
  );
};

export default SelectWeekModal;
